/**
 * THE navigation editing engine — the part that is not markup (#1466, ruling 50100).
 *
 * Ruling 50100 opens *"The current separate Personalize navigation screen is the
 * wrong interaction model"* and requires the bar itself to become editable IN
 * PLACE. That is a rendering question, and the answer is that the console draws
 * its own bar: `packages/ui` knows nothing about `@tanstack/react-router`,
 * `EntityIcon` or `Nav.module.css`, and it may not learn.
 *
 * So the component was cut where it was already separable. Everything below this
 * line was `NavigationEditor`'s body and none of it is markup: the working rows,
 * the ONE move reducer both routes go through, grab mode, the live-region
 * sentences, sections, the hide flags and the `held` signal. `NavigationEditor`
 * still renders exactly what it rendered — it is now one consumer of this hook —
 * and `apps/console`'s in-place bar is the second. **Neither owns the rules.**
 *
 * The seam is PROPS, never JSX. `gripProps` hands back the attributes a grip
 * must carry (its accessible name, `aria-describedby`, `aria-pressed`, the
 * keydown/blur/drag handlers and the ref that keeps focus across a move) and
 * each host spreads them onto its own element. That is what makes the two
 * surfaces the same behaviour rather than the same intention.
 *
 * ── what did NOT move, and why ─────────────────────────────────────────────
 * The rung strip (`presentRungs`, `attribution`) stays in `NavigationEditor`.
 * It is that sheet's own presentation of the ladder; the console's in-place mode
 * names the edited layer in its dock instead, because a rung list does not fit a
 * 340px sidebar and the sentence a person needs there is one line long. Both
 * read the same `NavLayer[]` from their host.
 *
 * Every measured fact the old body carried is carried here verbatim — the
 * `moving` ref that survives the blur a re-parented row fires, the `notify` ref
 * that stops an inline `onGrabChange` from re-triggering its own effect, the
 * `cancelled` ref that tells an Escaped rename from a blurred one, and the
 * refusal (not clamp) semantics of `applyMove`. Their reasons are on the lines
 * themselves.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  type NavDocument, type NavEditGroup,
  documentFrom, moveItem, moved, navigationEditModel, newSectionId,
} from './navigationDocument'

/** What a section is called before its author has said. Named, so a test and the
 *  announcement cannot disagree about it. */
export const NEW_SECTION = 'New section'

/** Named by every grip, so the gesture is discoverable without being repeated
 *  thirty-seven times into the accessibility tree. */
export const NAV_EDIT_INSTRUCTIONS =
  'Press Space to pick this up, then the up and down arrows to move it. '
  + 'Press Space again to drop it, or Escape to put it back. '
  + 'A link may move into any section.'

/**
 * WHAT is being moved, and WHERE it is going. A group is addressed by its index
 * among the groups; an item by its index inside a group named by ITS index —
 * which is what lets a destination sit in a different section from the source.
 * One shape for all three routes, so there is one `applyMove` and no second
 * implementation to keep in step.
 */
export type NavEditTarget =
  | { kind: 'group'; index: number }
  | { kind: 'item'; gi: number; index: number }

/** A target that has been picked up, remembering where it started (for Escape). */
type Grab = NavEditTarget & { home: NavEditTarget }

function sameTarget(a: NavEditTarget, b: NavEditTarget | null): boolean {
  if (b == null || a.kind !== b.kind) return false
  if (a.kind === 'group') return a.index === (b as Extract<NavEditTarget, { kind: 'group' }>).index
  const other = b as Extract<NavEditTarget, { kind: 'item' }>
  return a.gi === other.gi && a.index === other.index
}

export interface NavEditOptions {
  /**
   * The bar this edit sits on top of — the authority on what exists and on what
   * each link is called. For a USER pin the host passes `layerBelow(layers)`
   * (owner ruling O3), not the platform recipe.
   */
  base: NavDocument
  /** The document in force for this scope, if any. Supplies order, hidden flags and sections. */
  value?: NavDocument | null
  /** Disables every affordance while a save is in flight. */
  busy?: boolean
  /**
   * Fired whenever a row is HELD — picked up in grab mode, or having its section
   * name typed. A host that renders inside a dismissable overlay MUST use it:
   * Radix's dismissable layer registers Escape on `ownerDocument` with
   * `{ capture: true }`, so it always beats a bubble handler in here.
   */
  onGrabChange?: (held: boolean) => void
}

/** Attributes a host spreads onto ITS grip element. No JSX crosses this seam. */
export interface NavGripProps {
  'data-testid': string
  'aria-label': string
  'aria-describedby': string
  'aria-pressed': boolean
  draggable: boolean
  disabled: boolean
  ref: (el: HTMLButtonElement | null) => void
  onDragStart: (e: React.DragEvent) => void
  onDragEnd: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  onBlur: () => void
}

/** Attributes a host spreads onto a droppable row or list. */
export interface NavDropProps {
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
}

/** Attributes a host spreads onto the inline section-name field. */
export interface NavRenameProps {
  value: string
  autoFocus: true
  disabled: boolean
  'aria-label': string
  'data-testid': string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur: () => void
}

export interface NavEditController {
  /** The working order. Render THIS, never `value` — it is reconciled against `base`. */
  rows: NavEditGroup[]
  /** The last thing that happened, for a `role="status"` live region. */
  said: string
  /** True while a row is picked up or a section name is being typed. */
  held: boolean
  /** Current held row, so its disclosure ancestor cannot disappear. */
  heldTarget: NavEditTarget | null
  /** Save is refused while governed rows still require a real parent. */
  canSave: boolean
  recoveryCount: number
  excludedCount: number
  /** True when the working order differs from what the host handed in. */
  dirty: boolean
  /** How many links the saved bar would show. Zero is a reachable, sayable state. */
  visibleCount: number
  /** The id a host must put on the element holding {@link NAV_EDIT_INSTRUCTIONS}. */
  instructionsId: string
  /** The id of the section whose name is being typed, if any. */
  renaming: string | null
  isGrabbed: (t: NavEditTarget) => boolean
  isDragging: (t: NavEditTarget) => boolean
  gripProps: (t: NavEditTarget, label: string) => NavGripProps
  dropProps: (t: NavEditTarget) => NavDropProps
  listDropProps: (gi: number) => NavDropProps
  renameProps: (gi: number) => NavRenameProps
  /** The Move up / Move down route. Out of range is a refusal, never a clamp. */
  move: (t: NavEditTarget, to: NavEditTarget) => void
  setGroupHidden: (gi: number, hidden: boolean) => void
  setItemHidden: (gi: number, ii: number, hidden: boolean) => void
  addSection: () => void
  beginRename: (gi: number) => void
  deleteSection: (gi: number) => void
  /** The document to store. */
  document: () => NavDocument
}

export function useNavigationEdit(
  { base, value, busy, onGrabChange }: NavEditOptions,
): NavEditController {
  const initialModel = useMemo(() => navigationEditModel(base, value), [base, value])
  const initial = initialModel.rows
  const [rows, setRows] = useState<NavEditGroup[]>(initial)
  const [grab, setGrab] = useState<Grab | null>(null)
  const [drag, setDrag] = useState<NavEditTarget | null>(null)
  const [said, setSaid] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const instructionsId = useId()

  /**
   * A grabbed row keeps focus across the move, and the DOM node travels with it
   * — React reconciles by key, so it is the SAME element in a new position. This
   * flag exists because that move fires a `blur` in some engines, and dropping
   * on blur (which is the right thing when somebody tabs away) would otherwise
   * cancel the grab on the very first arrow press. A cross-section move DOES
   * remount the row under a different list, so the effect re-focuses by key
   * rather than relying on the node surviving.
   */
  const moving = useRef(false)
  const handles = useRef(new Map<string, HTMLButtonElement | null>())
  const handleKey = (t: NavEditTarget) => (t.kind === 'group' ? `g:${t.index}` : `i:${t.gi}:${t.index}`)

  useEffect(() => {
    if (!grab || !moving.current) return
    handles.current.get(handleKey(grab))?.focus()
    moving.current = false
  }, [grab])

  /* One place, rather than at all six `setGrab` call sites: a host that misses
     one of them gets a sheet that closes on Escape half the time, which is
     worse than one that always does.

     RENAMING counts as held, and the review is what found that it did not.
     Escape while typing a section name is a cancel of the RENAME — the field's
     own handler treats it as one — but Radix's document-capture listener had
     already dismissed the whole sheet by then, taking every unsaved reorder
     with it. Measured: Add section, type a name, Escape -> `onClose` called once.
     The grab case passed in the same probe, which is the tell: the protection
     was real and its condition was too narrow. */
  const held = grab !== null || renaming !== null
  /* The effect depends on the FACT, never on the callback's identity. A host
     that passes an inline arrow — which is the normal way to pass one — gives a
     new function every render, and if that callback also sets state the pair
     re-triggers each other forever. Found by writing the Modal seam test: the
     suite hung rather than failed, which is the worst way for a component to
     say no. The ref keeps the latest callback without making it a dependency. */
  const notify = useRef(onGrabChange)
  notify.current = onGrabChange
  useEffect(() => { notify.current?.(held) }, [held])

  const patchGroup = (gi: number, patch: Partial<NavEditGroup>) =>
    setRows((r) => r.map((g, i) => (i === gi ? { ...g, ...patch } : g)))
  const patchItem = (gi: number, ii: number, hidden: boolean) =>
    setRows((r) => r.map((g, i) => (i === gi
      ? { ...g, items: g.items.map((it, j) => (j === ii ? { ...it, hidden } : it)) }
      : g)))

  const labelFor = (t: NavEditTarget) =>
    (t.kind === 'group' ? rows[t.index]!.label : rows[t.gi]!.items[t.index]!.label)

  /**
   * THE single mutation. Every route — the Move buttons, grab mode, a pointer
   * drop — comes through here, and here goes through `moved()` for a group and
   * `moveItem()` for an item. Out of range is a refusal rather than a clamp:
   * Move-up on the first row must do nothing, not re-announce position 1 as if
   * something had happened. A cross-section destination may sit one past the end
   * of its list, because appending to a section is a real destination and
   * appending to an EMPTY one is the only way an invented section fills up.
   */
  const applyMove = (t: NavEditTarget, d: NavEditTarget): boolean => {
    if (t.kind !== d.kind) return false
    if (t.kind === 'group' && d.kind === 'group') {
      if (d.index < 0 || d.index >= rows.length || d.index === t.index) return false
      if (rows[t.index]?.recovery || rows[d.index]?.recovery) return false
      setRows((r) => moved(r, t.index, d.index))
      return true
    }
    if (t.kind !== 'item' || d.kind !== 'item') return false
    const dst = rows[d.gi]
    if (!dst) return false
    if (dst.recovery && d.gi !== t.gi) return false
    const same = d.gi === t.gi
    if (d.index < 0 || d.index > dst.items.length - (same ? 1 : 0)) return false
    if (same && d.index === t.index) return false
    setRows((r) => moveItem(r, { gi: t.gi, index: t.index }, { gi: d.gi, index: d.index }))
    return true
  }

  /**
   * Items name their DESTINATION section: "position 2 of 5" is ambiguous across
   * six lists, and after a cross-section move naming the section it left would
   * be actively wrong. The total counts the arriving row, which is not yet in
   * `rows` when the sentence is built.
   */
  const where = (t: NavEditTarget, d: NavEditTarget) => {
    if (d.kind === 'group') return `position ${d.index + 1} of ${rows.length}`
    const crossing = t.kind === 'item' && t.gi !== d.gi
    return `position ${d.index + 1} of ${rows[d.gi]!.items.length + (crossing ? 1 : 0)} in ${rows[d.gi]!.label}`
  }

  const moveAndSay = (t: NavEditTarget, d: NavEditTarget): boolean => {
    const sentence = `${labelFor(t)} moved to ${where(t, d)}`
    if (!applyMove(t, d)) return false
    setSaid(sentence)
    return true
  }

  /**
   * One arrow press, as a destination. For an item the list it walks is the
   * whole bar, not one section: past the end of a section is the top of the next
   * one. `rows[gi].items.length` as an up-destination is deliberate — it appends
   * at the bottom of the section being entered from below.
   */
  const step = (t: NavEditTarget, d: 1 | -1): NavEditTarget | null => {
    if (t.kind === 'group') {
      const to = t.index + d
      return to < 0 || to >= rows.length ? null : { kind: 'group', index: to }
    }
    const to = t.index + d
    if (to >= 0 && to < rows[t.gi]!.items.length) return { kind: 'item', gi: t.gi, index: to }
    const gi = t.gi + d
    if (gi < 0 || gi >= rows.length) return null
    return { kind: 'item', gi, index: d === 1 ? 0 : rows[gi]!.items.length }
  }

  const onHandleKeyDown = (t: NavEditTarget) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const isHeld = sameTarget(t, grab)
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      if (isHeld) {
        setSaid(`${labelFor(t)} dropped at ${where(t, t)}.`)
        setGrab(null)
      } else {
        setGrab({ ...t, home: t })
        setSaid(`${labelFor(t)} grabbed. Use the up and down arrows to move it, then press Space to drop.`)
      }
      return
    }
    if (!isHeld) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const dest = step(t, e.key === 'ArrowDown' ? 1 : -1)
      if (!dest) return
      moving.current = true
      if (moveAndSay(t, dest)) setGrab({ ...dest, home: grab!.home })
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      // Back where it started, through the same reducer — a cancel that
      // reconstructed the order by hand would be a second implementation of the
      // one thing this hook does. `home` is a full target, so a row that
      // crossed into another section goes back to the section it came from too.
      const label = labelFor(t)
      const home = grab!.home
      const back = home.kind === 'item' && t.kind === 'item' && home.gi !== t.gi
        ? `position ${home.index + 1} in ${rows[home.gi]!.label}`
        : `position ${home.index + 1}`
      moving.current = true
      applyMove(t, home)
      setSaid(`Move cancelled. ${label} is back at ${back}.`)
      setGrab(null)
    }
  }

  /** Tabbing away drops the row where it is; it does not put it back. */
  const onHandleBlur = () => { if (!moving.current) setGrab(null) }

  const gripProps = (t: NavEditTarget, label: string): NavGripProps => ({
    'data-testid': 'nav-drag-handle',
    'aria-label': `Reorder ${label}`,
    'aria-describedby': instructionsId,
    'aria-pressed': sameTarget(t, grab),
    draggable: !busy,
    disabled: Boolean(busy),
    ref: (el) => { handles.current.set(handleKey(t), el) },
    onDragStart: (e) => { setDrag(t); e.dataTransfer.effectAllowed = 'move' },
    onDragEnd: () => setDrag(null),
    onKeyDown: onHandleKeyDown(t),
    onBlur: onHandleBlur,
  })

  /**
   * A drop lands the dragged row at the index of whatever it was dropped on.
   * Groups still accept only groups — a section inside a section is not a shape
   * the document has. Items accept items from ANY section, which is the whole of
   * ruling M6: `documentFrom` can express the membership because `rowsFor` no
   * longer treats the base's partition as authority, and the route itself still
   * has to have come from the base.
   */
  const dropProps = (t: NavEditTarget): NavDropProps => ({
    onDragOver: (e) => {
      if (!drag || drag.kind !== t.kind) return
      e.preventDefault()
    },
    onDrop: (e) => {
      if (!drag || drag.kind !== t.kind) return
      e.preventDefault()
      // Stopped, or a drop on an item ALSO reaches the enclosing list's
      // append-to-the-end target and the row moves twice for one gesture.
      e.stopPropagation()
      moveAndSay(drag, t)
      setDrag(null)
    },
  })

  /** The list itself is a target, so an EMPTY invented section can be filled. */
  const listDropProps = (gi: number): NavDropProps => ({
    onDragOver: (e) => {
      if (!drag || drag.kind !== 'item') return
      e.preventDefault()
    },
    onDrop: (e) => {
      if (!drag || drag.kind !== 'item') return
      e.preventDefault()
      e.stopPropagation()
      moveAndSay(drag, { kind: 'item', gi, index: rows[gi]!.items.length })
      setDrag(null)
    },
  })

  const addSection = () => {
    const id = newSectionId()
    setRows((r) => [...r, { id, label: NEW_SECTION, hidden: false, items: [] }])
    setDraft(NEW_SECTION)
    setRenaming(id)
    setSaid(`${NEW_SECTION} added at position ${rows.length + 1} of ${rows.length + 1}. Type a name, then press Enter.`)
  }

  /* Escape leaves the field without committing, and removing a focused input can
     fire `blur` — so the cancel has to be remembered rather than inferred. */
  const cancelled = useRef(false)
  const commitRename = (gi: number) => {
    if (cancelled.current) { cancelled.current = false; setRenaming(null); return }
    const g = rows[gi]
    setRenaming(null)
    if (!g?.id) return
    const next = draft.trim()
    // A section with no name is a heading nobody can read and nothing can
    // announce; the old name is kept and the refusal is said out loud.
    if (!next) { setSaid(`${g.label} kept its name — a section needs one.`); return }
    if (next === g.label) return
    patchGroup(gi, { label: next })
    setSaid(`Section renamed to ${next}.`)
  }

  const renameProps = (gi: number): NavRenameProps => ({
    value: draft,
    autoFocus: true,
    disabled: Boolean(busy),
    'aria-label': 'Section name',
    'data-testid': 'nav-section-name',
    onChange: (e) => setDraft(e.target.value),
    onKeyDown: (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commitRename(gi) }
      else if (e.key === 'Escape') {
        /* `stopPropagation` does NOT save an overlay host, and believing it did
           is what let this ship: Radix listens on ownerDocument in the CAPTURE
           phase, so it has already run and already dismissed before this bubble
           handler exists. What refuses the dismissal is the host reading `held`
           — which is why `renaming` had to join `grab` in it. This stays because
           cancelling the rename is still the right local act and the keystroke
           should not also reach anything above. */
        e.preventDefault(); e.stopPropagation()
        cancelled.current = true
        setRenaming(null)
      }
    },
    onBlur: () => commitRename(gi),
  })

  const deleteSection = (gi: number) => {
    const g = rows[gi]
    if (!g?.id || g.recovery) return
    if (g.items.length) {
      setSaid(`${g.label} still holds ${g.items.length} link${g.items.length === 1 ? '' : 's'} — move ${g.items.length === 1 ? 'it' : 'them'} out first.`)
      return
    }
    setRows((r) => r.filter((_, i) => i !== gi))
    setSaid(`${g.label} deleted.`)
  }

  const visibleCount = rows.reduce(
    (n, g) => n + (g.hidden || g.recovery ? 0 : g.items.filter((i) => !i.hidden).length), 0)
  const recoveryCount = rows.find((group) => group.recovery)?.items.length ?? 0
  const canSave = recoveryCount === 0

  /* Compared as DOCUMENTS rather than as row arrays: `rowsFor` normalises (it
     re-adds dropped base sections and re-homes stray routes), so the rows a host
     is handed are already not the stored document, and comparing arrays would
     report a bar as dirty the moment it was opened. Ruling 50100 row 3 turns on
     this being right — it is what decides whether Escape asks before discarding. */
  const dirty = useMemo(
    () => JSON.stringify(documentFrom(rows.filter((group) => !group.recovery)))
      !== JSON.stringify(documentFrom(initial.filter((group) => !group.recovery))),
    [rows, initial])

  const heldTarget: NavEditTarget | null = grab
    ? (grab.kind === 'group' ? { kind: 'group', index: grab.index }
      : { kind: 'item', gi: grab.gi, index: grab.index })
    : renaming == null ? null : {
      kind: 'group', index: rows.findIndex((group) => group.id === renaming),
    }

  return {
    rows, said, held, heldTarget, dirty, visibleCount, canSave, recoveryCount,
    excludedCount: initialModel.excluded.length, instructionsId, renaming,
    isGrabbed: (t) => sameTarget(t, grab),
    isDragging: (t) => sameTarget(t, drag),
    gripProps, dropProps, listDropProps, renameProps,
    move: moveAndSay,
    setGroupHidden: (gi, hidden) => {
      if (rows[gi]?.recovery) return
      patchGroup(gi, { hidden })
      setSaid(`${rows[gi]!.label} ${hidden ? 'hidden' : 'shown'}`)
    },
    setItemHidden: (gi, ii, hidden) => {
      patchItem(gi, ii, hidden)
      setSaid(`${rows[gi]!.items[ii]!.label} ${hidden ? 'hidden' : 'shown'}`)
    },
    addSection,
    beginRename: (gi) => { const g = rows[gi]; if (g?.id && !g.recovery) { setDraft(g.label); setRenaming(g.id) } },
    deleteSection,
    document: () => documentFrom(rows),
  }
}
